import React, { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { PageHeader } from "@/components";
import { developmentApi } from "@/lib/api";
import {
    DevelopmentsTable,
    type DevelopmentRow,
    PRIORITY_CONFIG,
} from "@/pages/Developments/DevelopmentsTable";
import { NewDevelopmentModal } from "@/pages/Developments/NewDevelopmentModal";
import { Select } from "@/components/Select";
import { StatCard } from "@/components/StatCard";
import { Tabs } from "@/components/Tabs";

export const Developments: React.FC = () => {
    const { t } = useTranslation("global");
    const [items, setItems] = useState<DevelopmentRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    type DevelopmentTab = "open" | "closed" | "all";

    const getInitialTab = (): DevelopmentTab => {
        if (typeof window !== "undefined") {
            const path = window.location.pathname.replace(/\/+$/, "");
            if (path.endsWith("/closed")) {
                return "closed";
            }
            if (path.endsWith("/all")) {
                return "all";
            }
        }

        return "open";
    };

    const [activeTab, setActiveTab] = useState<DevelopmentTab>(getInitialTab);
    const [search, setSearch] = useStoredPreference(
        "vigepra_developments_search",
        z.string().max(500),
        "",
        true
    );
    const [statusFilter, setStatusFilter] = useStoredPreference(
        "vigepra_developments_status",
        z.array(z.string()),
        [],
        true
    );
    const [priorityFilter, setPriorityFilter] = useStoredPreference(
        "vigepra_developments_priority",
        z.array(z.string()),
        [],
        true
    );
    const [assigneeFilter, setAssigneeFilter] = useStoredPreference(
        "vigepra_developments_assignee",
        z.array(z.string()),
        [],
        true
    );
    const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false);

    const fetchDevelopments = async (): Promise<void> => {
        setLoading(true);
        try {
            const res = await developmentApi.list({});
            if (res.result) {
                setItems(res.data);
            }
        } catch {
            // handle error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDevelopments();
    }, []);

    const handleUpdateItem = (updated: DevelopmentRow): void => {
        setItems((prev) =>
            prev.map((item) =>
                item.id === updated.id ? { ...item, ...updated } : item
            )
        );
    };

    const handleDeleteItem = (deletedId: number): void => {
        setItems((prev) => prev.filter((item) => item.id !== deletedId));
    };

    // Helper status checkers
    const isClosedStatus = (s?: string): boolean =>
        s === "completed" ||
        s === "cancelled" ||
        s === "completado" ||
        s === "cancelado";
    const isCompletedStatus = (s?: string): boolean =>
        s === "completed" || s === "completado";

    // KPI Metrics calculation
    const totalCount = items.length;
    const openCount = items.filter((i) => !isClosedStatus(i.status)).length;
    const closedCount = items.filter((i) => isClosedStatus(i.status)).length;
    const activeCount = items.filter((i) =>
        [
            "request",
            "planning",
            "development",
            "testing",
            "solicitud",
            "diseno",
            "planificacion",
            "desarrollo",
            "pruebas",
        ].includes(i.status)
    ).length;
    const validationCount = items.filter(
        (i) =>
            i.status === "client_validation" ||
            i.status === "validacion_cliente"
    ).length;
    const completedCount = items.filter((i) =>
        isCompletedStatus(i.status)
    ).length;

    const tabItems = [
        {
            id: "open" as const,
            label: t("developments.tab_open"),
            badge: openCount,
        },
        {
            id: "closed" as const,
            label: t("developments.tab_closed"),
            badge: closedCount,
        },
        {
            id: "all" as const,
            label: t("developments.tab_all"),
            badge: totalCount,
        },
    ];

    const handleTabChange = (newTab: DevelopmentTab): void => {
        setActiveTab(newTab);
        if (newTab === "open") {
            if (statusFilter.some(isClosedStatus)) {
                setStatusFilter([]);
            }
        } else {
            if (newTab === "closed") {
                if (statusFilter.length > 0 && statusFilter.some((value) => !isClosedStatus(value))) {
                    setStatusFilter([]);
                }
            }
        }
    };

    const navigateToTab = (newTab: DevelopmentTab) => {
        handleTabChange(newTab);
        if (typeof window !== "undefined") {
            window.history.pushState(
                { ...window.history.state, tab: newTab },
                "",
                `/developments/${newTab}${window.location.search}${window.location.hash}`
            );
        }
    };

    const statusOptions = useMemo(() => {
        const openOptions = [
            { value: "request", label: t("statuses.request") },
            { value: "planning", label: t("statuses.planning") },
            { value: "development", label: t("statuses.development") },
            { value: "testing", label: t("statuses.testing") },
            {
                value: "client_validation",
                label: t("statuses.client_validation"),
            },
        ];

        const closedOptions = [
            { value: "completed", label: t("statuses.completed") },
            { value: "cancelled", label: t("statuses.cancelled") },
        ];

        let list = [];
        switch (activeTab) {
            case "open":
                list = openOptions;
                break;
            case "closed":
                list = closedOptions;
                break;
            case "all":
            default:
                list = [...openOptions, ...closedOptions];
                break;
        }

        const availableStatuses = new Set(items.map((item) => item.status));

        return list.filter((option) => availableStatuses.has(option.value));
    }, [activeTab, items, t]);

    const priorityOptions = useMemo(() => {
        const available = new Set(
            items.map((item) => item.priority).filter(Boolean)
        );
        const options = (["low", "medium", "high", "urgent"] as const)
            .filter((key) => available.has(key))
            .map((key) => {
                const cfg = PRIORITY_CONFIG[key];

                return {
                    value: key,
                    label: t(`priorities.${key}`),
                    iconPosition: "left" as const,
                    icon: (
                        <span
                            className={`w-14 shrink-0 inline-flex items-center justify-start gap-0 ${cfg.color}`}
                        >
                            {Array.from({ length: cfg.arrows }).map((_, i) => (
                                <Icon
                                    key={i}
                                    value="arrowUp"
                                    size={14}
                                    strokeWidth={3}
                                />
                            ))}
                        </span>
                    ),
                };
            });

        if (items.some((item) => !item.priority)) {
            options.unshift({
                value: "__empty__",
                label: t("developments.filter_empty"),
                iconPosition: "left" as const,
                icon: null,
            });
        }

        return options;
    }, [items, t]);

    const assigneeOptions = useMemo(() => {
        const uniqueAssignees: { value: string; label: string }[] = [];
        const map = new Map<string, boolean>();
        items.forEach((t) => {
            if (t.assigned && t.assigned.name && !map.has(t.assigned.name)) {
                map.set(t.assigned.name, true);
                uniqueAssignees.push({
                    value: t.assigned.name,
                    label: t.assigned.name,
                });
            }
        });
        uniqueAssignees.sort((a, b) => a.label.localeCompare(b.label));
        return uniqueAssignees;
    }, [items, t]);

    const displayedItems = useMemo(() => {
        return items.filter((item) => {
            if (activeTab === "open") {
                if (
                    item.status === "completed" ||
                    item.status === "cancelled" ||
                    item.status === "completado" ||
                    item.status === "cancelado"
                ) {
                    return false;
                }
            } else {
                if (activeTab === "closed") {
                    if (
                        item.status !== "completed" &&
                        item.status !== "cancelled" &&
                        item.status !== "completado" &&
                        item.status !== "cancelado"
                    ) {
                        return false;
                    }
                }
            }

            if (statusFilter.length > 0 && !statusFilter.includes(item.status)) {
                return false;
            }

            if (
                priorityFilter.length > 0 &&
                !priorityFilter.some((value) =>
                    value === "__empty__"
                        ? !item.priority
                        : value === item.priority
                )
            ) {
                return false;
            }

            if (assigneeFilter.length > 0 && !assigneeFilter.includes(item.assigned?.name || "")) {
                return false;
            }

            if (search) {
                const s = search.toLowerCase();
                const code = item.code?.toLowerCase() || "";
                const title = item.title?.toLowerCase() || "";
                const desc = item.description?.toLowerCase() || "";

                if (
                    !code.includes(s) &&
                    !title.includes(s) &&
                    !desc.includes(s)
                ) {
                    return false;
                }
            }

            return true;
        });
    }, [
        items,
        activeTab,
        statusFilter,
        priorityFilter,
        assigneeFilter,
        search,
    ]);

    return (
        <div className="w-full space-y-3 animate-fade-in">
            <PageHeader
                compact
                className="!mb-0"
                title={t("developments.title")}
                action={{
                    label: t("developments.new_button"),
                    icon: "plus",
                    onClick: () => setIsNewModalOpen(true),
                }}
            />

            {/* KPI Cards */}
            {/*
            <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 mb-4`}>                

                <StatCard
                    title={t('developments.kpi_active')}
                    value={activeCount}
                    icon="clock"
                    //colorClass="bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-200/60 dark:border-amber-700/60"
                    colorClass="text-amber-600 dark:text-amber-400"
                    valueColorClass="text-amber-600 dark:text-amber-400"
                    onClick={() => {
                        navigateToTab('open');
                        setStatusFilter('');
                        setStatusFilter('');
                        setPriorityFilter('');
                        setAssigneeFilter('');
                    }}
                />

                <StatCard
                    title={t('developments.kpi_validation')}
                    value={validationCount}
                    icon="alertCircle"
                    //colorClass="bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200/60 dark:border-rose-700/60"
                    colorClass="text-rose-600 dark:text-rose-400"
                    valueColorClass="text-rose-600 dark:text-rose-400"
                    onClick={() => {
                        navigateToTab('open');
                        setStatusFilter('client_validation');
                        setPriorityFilter('');
                        setAssigneeFilter('');
                    }}
                />

                <StatCard
                    title={t('developments.kpi_closed')}
                    value={closedCount}
                    icon="checkCircle"
                    //colorClass="bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-700/60"
                    colorClass="text-emerald-600 dark:text-emerald-400"
                    valueColorClass="text-emerald-600 dark:text-emerald-400"
                    onClick={() => {
                        navigateToTab('closed');
                        setStatusFilter('');
                        setPriorityFilter('');
                        setAssigneeFilter('');
                    }}
                />

                <StatCard
                    title={t('developments.kpi_total')}
                    value={totalCount}
                    icon="layers"
                    //colorClass="bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 border border-slate-200/50 dark:border-slate-700/60"
                    colorClass="text-slate-700 dark:text-slate-200"
                    onClick={() => {
                        navigateToTab('all');
                        setStatusFilter('');
                        setPriorityFilter('');
                    }}
                />
            </div>
            */}

            {/* Navigation Tabs */}
            <Tabs<DevelopmentTab>
                className="mb-4"
                tabs={tabItems}
                activeTab={activeTab}
                onChange={handleTabChange}
                variant="underline"
                scroll={true}
                syncUrl={true}
                urlPrefix="/developments"
                urlDefaultTab="open"
            />

            {/* Developments Table with Integrated Filters */}
            <DevelopmentsTable
                items={displayedItems}
                loading={loading}
                search={search}
                onSearchChange={setSearch}
                viewFilters={{
                    status: statusFilter.join(","),
                    priority: priorityFilter.join(","),
                    assignee: assigneeFilter.join(","),
                }}
                viewContext={activeTab}
                onRestoreView={(filters, context) => {
                    setStatusFilter(filters.status ? filters.status.split(",") : []);
                    setPriorityFilter(filters.priority ? filters.priority.split(",") : []);
                    setAssigneeFilter(filters.assignee ? filters.assignee.split(",") : []);
                    if (
                        context === "open" ||
                        context === "closed" ||
                        context === "all"
                    ) {
                        navigateToTab(context);
                    }
                }}
                onUpdateItem={handleUpdateItem}
                onDeleteItem={handleDeleteItem}
                onRefresh={fetchDevelopments}
                emptyText={
                        search || statusFilter.length || priorityFilter.length || assigneeFilter.length
                        ? t("developments.empty_title_filtered")
                        : undefined
                }
                filterElement={
                    <>
                        <Select
                            multiple
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={statusOptions}
                            placeholder={t("developments.filter_status")}
                            className="w-full sm:flex-1 sm:min-w-0 lg:flex-initial lg:w-auto lg:min-w-[170px]"
                        />

                        <Select
                            multiple
                            value={priorityFilter}
                            onChange={setPriorityFilter}
                            options={priorityOptions}
                            placeholder={t("developments.filter_priority")}
                            className="w-full sm:flex-1 sm:min-w-0 lg:flex-initial lg:w-auto lg:min-w-[190px]"
                        />

                        <Select
                            searchable
                            multiple
                            value={assigneeFilter}
                            onChange={setAssigneeFilter}
                            options={assigneeOptions}
                            placeholder={t("developments.filter_assignee")}
                            className="w-full sm:flex-1 sm:min-w-0 lg:flex-initial lg:w-auto lg:min-w-[190px]"
                        />
                    </>
                }
                filterFooter={
                    search ||
                    statusFilter.length ||
                    priorityFilter.length ||
                    assigneeFilter.length ? (
                        <div className="flex items-center justify-center w-full mt-2 sm:mt-1">
                            <button
                                type="button"
                                onClick={() => {
                                    setSearch("");
                                    setStatusFilter([]);
                                    setPriorityFilter([]);
                                    setAssigneeFilter([]);
                                }}
                                className="text-xs font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 transition-colors flex items-center gap-1 cursor-pointer"
                            >
                                <Icon value="x" size={12} strokeWidth={3} />
                                <span>{t("filters.clear")}</span>
                            </button>
                        </div>
                    ) : undefined
                }
            />

            {/* New Development Modal */}
            <NewDevelopmentModal
                isOpen={isNewModalOpen}
                onClose={() => setIsNewModalOpen(false)}
                onSuccess={() => fetchDevelopments()}
            />
        </div>
    );
};
